import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IGameBarApiProvider} from "data/providers/api/gamebar.api.provider";
import {GameLevelStatus, GameLevel} from "data/enums";
import {wait} from "data/utils";
import {shuffle} from "lodash";

export interface IGameBar {
	contestPoints: number | null;
	contestRank: number | null;
	roundPoints: number | null;
	roundRank: number | null;
	level: GameLevel | null;
	levelStatus: GameLevelStatus | null;
}

export interface IGameBarStore {
	get isLoading(): boolean;

	get gameBar(): IGameBar | undefined;

	fetchGameBar(contestId: number): Promise<void>;
	fetchGameBarLive(): Promise<void>;
}

@injectable()
export class GameBarStore implements IGameBarStore {
	@observable private _isLoading: boolean = false;
	@observable private _gameBar: IGameBar | undefined;
	@observable private _lastContestId: number = 0;

	constructor(
		@inject(Bindings.GameBarApiProvider) private _gameBarApiProvider: IGameBarApiProvider
	) {
		makeAutoObservable(this);
	}

	get gameBar(): IGameBar | undefined {
		return this._gameBar;
	}

	get isLoading(): boolean {
		return this._isLoading;
	}

	@action
	public async fetchGameBar(contestId: number, isLive?: boolean): Promise<void> {
		if (this._lastContestId === contestId && !isLive) {
			return;
		}
		try {
			this._isLoading = true;
			const {data} = await this._gameBarApiProvider.gameBar(contestId);

			runInAction(() => {
				this._gameBar = data.success.gamebar;
				this._lastContestId = contestId;
			});
		} catch (e) {
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

	@action
	async fetchGameBarLive() {
		if (this._lastContestId) {
			const delays = [500, 1000, 1500, 2000];
			await wait(shuffle(delays)[0]);
			await this.fetchGameBar(this._lastContestId, true);
		}
	}
}
